import { AD_HANDOFF_EXTENSION_TYPE, } from '@apm/contracts';
/**
 * VAST 광고 어댑터 (v21 F10).
 *
 * **코어에 둘 수 없는 이유가 둘이다** — 파싱이 `DOMParser` 를 부르고 트래킹이 픽셀 GET
 * 이다. `tsconfig.core.json` 이 `lib: ES2022`·`types: []` 로 둘 다 막는다(절대 규칙 6).
 *
 * **`NetworkAdapter` 를 넓히지 않았다.** 그쪽은 `get<T>` 가 JSON 전제라 XML 이 오지
 * 못하고, 넓히면 iOS/Android 이식의 근거가 약해진다 (절대 규칙 36) — 이것은 **갈아 끼울
 * 수 있는 어댑터**이지 넓어진 계약이 아니다.
 *
 * 안이 셋으로 나뉘고 **앞의 셋은 전부 순수 함수**다 — 그래야 픽스처 XML 로 단위 테스트가
 * 붙는다.
 */
// ---------------------------------------------------------------------------
// 1. 요청 — **상용 서버로 갈 때 갈리는 자리가 여기 하나다**
// ---------------------------------------------------------------------------
/**
 * Ad Request URL. **순수 함수다.**
 *
 * **요청에는 표준이 없다** — IAB 가 표준화한 것은 응답(VAST)이지 요청이 아니다.
 * 아래 이름은 GAM 관례를 차용한 우리 것이고, **상용 서버로 갈 때 갈리는 것이 이 함수
 * 하나**다 (`docs/45` §7.2).
 *
 * `correlator` 를 **인자로 받는다** — 함수 안에서 만들면 「매번 다르다」를 단위 테스트가
 * 못박을 수 없고, 같은 입력에 같은 출력이라는 성질도 잃는다. 코어가 채운다.
 */
export function buildTagUrl(req) {
    const base = req.adServerUrl.replace(/\/+$/, '');
    const params = new URLSearchParams({
        ch: req.channelId,
        pod: req.breakKind,
        pd: String(req.maxDurationSec),
        ep: req.productId,
        pos: String(req.positionSec),
        // ★ v21 확장. **응답을 바꾸는 파라미터다** — 아래 `dev`·`pid` 같은 상관관계 키가
        // 아니라, 광고 서버가 이 값으로 후보를 고른다.
        lang: req.language,
        dev: req.deviceId,
        pid: req.playlistId,
        correlator: String(req.correlator),
    });
    // 스킵 재보충(`docs/45` §17) — 처음 요청에는 싣지 않는다(태그가 바이트 그대로다)
    if (req.excludeCreativeIds !== undefined && req.excludeCreativeIds.length > 0) {
        params.set('acs', req.excludeCreativeIds.join(','));
    }
    if (req.refill === true)
        params.set('_ext', '1');
    return `${base}/vast?${params.toString()}`;
}
// ---------------------------------------------------------------------------
// 2. 파싱 — 전부 순수 함수
// ---------------------------------------------------------------------------
/**
 * `skipoffset` 을 초로. **두 표기를 다 받는다** (`docs/45` §8.8).
 *
 * 우리 서버는 `hh:mm:ss` 만 내지만 **상용 서버는 퍼센트를 낸다** — 퍼센트는
 * `<Duration>` 에 곱해 초로 만든다.
 *
 * 못 읽으면 `null` 이고 그것은 **건너뛸 수 없는 광고**다. 던지지 않는다.
 */
export function parseSkipOffset(raw, durationSec) {
    if (raw === null)
        return null;
    const value = raw.trim();
    if (value === '')
        return null;
    if (value.endsWith('%')) {
        const percent = Number(value.slice(0, -1));
        if (!Number.isFinite(percent) || percent < 0)
            return null;
        return (durationSec * percent) / 100;
    }
    const seconds = parseClock(value);
    return seconds === null || seconds < 0 ? null : seconds;
}
/** `hh:mm:ss[.mmm]` → 초. 형식이 아니면 `null` */
function parseClock(value) {
    const match = /^(\d{1,2}):([0-5]\d):([0-5]\d)(\.\d{1,3})?$/.exec(value);
    if (!match)
        return null;
    const [, h, m, s, ms] = match;
    return Number(h) * 3600 + Number(m) * 60 + Number(s) + (ms === undefined ? 0 : Number(ms));
}
/**
 * VAST → `AdItem[]`. **순수 함수다** (`DOMParser` 는 호출자가 준다).
 *
 * **`<Wrapper>` 는 그 `<Ad>` 를 버린다** — 래퍼를 따라가면 요청이 몇 단계가 되고, 그것은
 * 이 MVP 의 범위 밖이다. 버리는 것이 조용하지 않은 이유는 **남은 광고로 pod 이 성립하기
 * 때문**이다(하나도 안 남으면 no fill 과 같은 길이다).
 *
 * **던지지 않는다.** 깨진 XML·빈 VAST·모르는 모양은 전부 빈 배열이다 (`docs/45` §8.7).
 */
export function parseVast(xml, parse) {
    let doc;
    try {
        doc = parse(xml);
    }
    catch {
        /*
         * ★ **파서가 던질 수도 있다.** 브라우저의 `DOMParser` 는 깨진 XML 에 예외 대신
         * `<parsererror>` 를 심지만 **그것이 모든 구현의 규약은 아니다** — `@xmldom/xmldom`
         * 은 던진다(단위 테스트가 그것으로 이 자리를 찾았다).
         *
         * 어느 쪽이든 **빈 배열이어야 한다.** 여기서 새면 광고 하나가 재생을 멈춘다.
         */
        return [];
    }
    if (doc === null)
        return [];
    // 브라우저의 `DOMParser` 는 깨진 XML 에 예외 대신 `<parsererror>` 를 심는다.
    if (doc.getElementsByTagName('parsererror').length > 0)
        return [];
    const items = [];
    for (const ad of Array.from(doc.getElementsByTagName('Ad'))) {
        // 래퍼는 버린다 — 따라가지 않는다.
        if (ad.getElementsByTagName('Wrapper').length > 0)
            continue;
        const inline = ad.getElementsByTagName('InLine')[0];
        if (!inline)
            continue;
        const item = parseInline(ad, inline);
        if (item !== null)
            items.push(item);
    }
    return items;
}
/**
 * v36 — **`<Ad>` 원문 하나 → `AdItem`** (절대 규칙 97 · `docs/72` §4.5).
 *
 * Flower 엔진은 광고마다 VAST 의 `<Ad>` 요소를 받은 그대로 넘긴다(Flower 의 광고 id 는 합성값이라 크리에이티브 id 로
 * 쓸 수 없다). 그것을 **BLOOM 과 같은 해석**(`parseVast` → `parseInline` · `parseHandoffExtension`)으로 읽는다 —
 * `parseVast` 는 문서 안의 `<Ad>` 를 찾으므로 루트가 `<Ad>` 인 문서도 그대로 된다. 떼어 낸 `<Ad>` 에는 VAST 루트의
 * 기본 이름공간 선언이 없지만 해석이 이름공간을 보지 않아(`getElementsByTagName`) 같은 값이 나온다.
 */
export function parseVastAd(adXml, parse = defaultParseXml) {
    return parseVast(adXml, parse)[0] ?? null;
}
function parseInline(ad, inline) {
    const linear = inline.getElementsByTagName('Linear')[0];
    if (!linear)
        return null;
    const durationSec = parseClock(text(linear.getElementsByTagName('Duration')[0]) ?? '');
    if (durationSec === null || durationSec <= 0)
        return null;
    const videoUrl = pickMediaFile(linear);
    if (videoUrl === null)
        return null;
    // **`<Ad id>` 가 우리 `Creative.id` 다** — 조립하지 않는다 (`docs/45` §14 의 20).
    const universalAdId = text(inline.getElementsByTagName('UniversalAdId')[0]);
    const creativeId = universalAdId ?? ad.getAttribute('id') ?? '';
    if (creativeId === '')
        return null;
    const clickThrough = text(linear.getElementsByTagName('ClickThrough')[0]) ??
        text(inline.getElementsByTagName('ClickThrough')[0]) ??
        '';
    const fallback = {
        productId: creativeId,
        title: text(inline.getElementsByTagName('AdTitle')[0]) ?? '',
        // **Extension 이 없으면 0 이고 화면이 가격을 숨긴다** (`docs/45` §7.6).
        price: 0,
        thumbnailUrl: '',
        advertiserId: text(inline.getElementsByTagName('Advertiser')[0]),
        couponCode: null,
        handoffChannelId: null,
        // **딥링크 대신 웹 랜딩으로 간다.** 그것이 강등의 실체다.
        landingUrl: clickThrough,
    };
    const handoff = parseHandoffExtension(inline, fallback);
    return {
        creativeId,
        title: text(inline.getElementsByTagName('AdTitle')[0]) ?? '',
        videoUrl,
        durationSec,
        skipOffsetSec: parseSkipOffset(linear.getAttribute('skipoffset'), durationSec),
        clickThrough,
        handoff: handoff.payload,
        handoffSource: handoff.source,
        tracking: parseTracking(inline, linear),
    };
}
/**
 * **HLS 를 고른다.** 여럿이면 `application/x-mpegURL` 이 먼저다 — progressive MP4 를
 * 고르면 두 어댑터가 `#EXTINF`/`LEVEL_LOADED` 로 `videoSpan` 을 얻지 못해 **예측 스왑을
 * 통째로 잃는다** (`docs/45` §7.3).
 */
function pickMediaFile(linear) {
    const files = Array.from(linear.getElementsByTagName('MediaFile'));
    const hls = files.find((f) => (f.getAttribute('type') ?? '').toLowerCase().includes('mpegurl'));
    const url = text(hls ?? files[0]);
    return url === null || url === '' ? null : url;
}
/**
 * `<Extension type="apm/handoff">` (`docs/45` §7.4).
 *
 * **없거나 버전을 모르면 폴백으로 떨어진다. 던지지 않는다.**
 *
 * `version` 을 두는 이유 — Extension 은 우리만 아는 것이라 언젠가 모양이 바뀐다.
 * **모르는 버전을 만나면 그 Extension 을 통째로 무시하는 것**이 계약이고, 그렇게 해야
 * 새 필드를 옛 파서가 반쯤 읽는 일이 없다.
 *
 * **폴백이 조용해서는 안 된다** — `source` 가 그것을 드러낸다 (`docs/45` §14 의 18).
 */
export function parseHandoffExtension(inline, fallback) {
    const extension = Array.from(inline.getElementsByTagName('Extension')).find((node) => node.getAttribute('type') === AD_HANDOFF_EXTENSION_TYPE);
    const handoff = extension?.getElementsByTagName('Handoff')[0];
    if (!handoff || handoff.getAttribute('version') !== '1') {
        return { payload: fallback, source: 'clickthrough' };
    }
    const deepLink = text(handoff.getElementsByTagName('DeepLink')[0]);
    const price = Number(text(handoff.getElementsByTagName('Price')[0]) ?? '');
    return {
        payload: {
            productId: text(handoff.getElementsByTagName('ProductId')[0]) ?? fallback.productId,
            title: fallback.title,
            price: Number.isFinite(price) && price >= 0 ? Math.floor(price) : 0,
            thumbnailUrl: text(handoff.getElementsByTagName('ThumbnailUrl')[0]) ?? '',
            advertiserId: fallback.advertiserId,
            couponCode: text(handoff.getElementsByTagName('CouponCode')[0]),
            handoffChannelId: text(handoff.getElementsByTagName('HandoffChannelId')[0]),
            // **`<DeepLink>` 가 비었으면 그것도 강등이다** — 폰이 열 곳이 없으면 웹으로 간다.
            landingUrl: deepLink ?? fallback.landingUrl,
        },
        source: deepLink === null ? 'clickthrough' : 'extension',
    };
}
function parseTracking(inline, linear) {
    const events = Array.from(linear.getElementsByTagName('Tracking'));
    const byEvent = (name) => events
        .filter((node) => node.getAttribute('event') === name)
        .map((node) => text(node))
        .filter((url) => url !== null && url !== '');
    return {
        impression: Array.from(inline.getElementsByTagName('Impression'))
            .map((node) => text(node))
            .filter((url) => url !== null && url !== ''),
        start: byEvent('start'),
        firstQuartile: byEvent('firstQuartile'),
        midpoint: byEvent('midpoint'),
        thirdQuartile: byEvent('thirdQuartile'),
        complete: byEvent('complete'),
        skip: byEvent('skip'),
        click: Array.from(linear.getElementsByTagName('ClickTracking'))
            .map((node) => text(node))
            .filter((url) => url !== null && url !== ''),
    };
}
/** 요소의 텍스트. CDATA 도 `textContent` 로 온다. 비었으면 `null` */
function text(node) {
    const value = node?.textContent?.trim();
    return value === undefined || value === '' ? null : value;
}
export class WebAdAdapter {
    fetchImpl;
    parseXml;
    sendPixel;
    constructor(opts = {}) {
        this.fetchImpl = opts.fetchImpl ?? ((...args) => fetch(...args));
        this.parseXml = opts.parseXml ?? defaultParseXml;
        this.sendPixel = opts.sendPixel ?? defaultSendPixel;
    }
    /**
     * **타임아웃을 호출자가 준다** — 예약형 10초, 즉시형 3초. 어댑터가 상수로 박으면
     * **프리롤에서 검은 화면이 10초** 간다 (`docs/45` §14 의 6). 프리롤과 미드롤은
     * **재생을 막느냐 아니냐가 다르고**, 그 사정을 아는 것은 코어다.
     *
     * **초과 뒤 늦게 온 응답은 `AbortController` 로 끊고 버린다** — 안 끊으면 이미
     * 콘텐츠가 재생 중인데 광고가 끼어든다.
     *
     * **실패는 빈 배열이다. 던지지 않는다** — 광고가 안 나오는 것은 시청자에게 사고가
     * 아니고, 코어가 `emit('error')` 하면 멀쩡히 재생 중인 채널에 오류 UI 가 뜬다.
     */
    async requestAds(req, opts) {
        if (req.adServerUrl.trim() === '')
            return [];
        const controller = new AbortController();
        const timer = setTimeout(() => {
            controller.abort();
        }, opts.timeoutMs);
        try {
            const response = await this.fetchImpl(buildTagUrl(req), {
                signal: controller.signal,
                // 광고 태그에는 자격이 없다 (`docs/45` §6.1). 쿠키를 싣지 않는다.
                credentials: 'omit',
                cache: 'no-store',
            });
            if (!response.ok)
                return [];
            return parseVast(await response.text(), this.parseXml);
        }
        catch {
            // 타임아웃(abort) · 네트워크 · 혼합 콘텐츠 차단 — **셋이 같은 길이다.**
            return [];
        }
        finally {
            clearTimeout(timer);
        }
    }
    /**
     * 트래킹 픽셀. **실패해도 조용하다** — 전송 실패가 재생을 방해해서는 안 된다는
     * `EventQueue` 의 규율 그대로다.
     */
    track(urls) {
        for (const url of urls) {
            try {
                this.sendPixel(url);
            }
            catch {
                // 광고가 안 세지는 것보다 재생이 끊기는 것이 나쁘다.
            }
        }
    }
}
function defaultParseXml(text) {
    try {
        return new DOMParser().parseFromString(text, 'text/xml');
    }
    catch {
        return null;
    }
}
/**
 * `Image` 로 쏜다. `fetch` 가 아닌 이유 — **응답을 기다릴 이유가 없고**, 픽셀은 CORS 를
 * 요구하지 않는다(광고 서버가 우리 것이 아닐 수 있다).
 */
function defaultSendPixel(url) {
    const pixel = new Image();
    pixel.src = url;
}
//# sourceMappingURL=vast-ad.js.map